import Dexie, { Table } from "dexie";
import { env } from "next-runtime-env";

/**
 * @file A Dexie.js-based module for caching images in IndexedDB.
 *
 * This module provides a simple and efficient way to store and retrieve images
 * downloaded from a URL, organized into groups. It handles fetching, metadata
 * extraction, and database operations.
 *
 */
export interface ImageRecord {
  group: string;
  key: string;
  identifier: string;
  itemSku: string;
  savedAt: number;
  source: string;
  width: number;
  height: number;
  order: number;
}

/**
 * Returns true when IndexedDB is available in the current environment.
 * Some in-app browsers (e.g. Instagram on iOS) do not expose indexedDB.
 */
export function isIndexedDBAvailable(): boolean {
  try {
    return typeof indexedDB !== "undefined" && indexedDB !== null;
  } catch {
    return false;
  }
}

/**
 * The Dexie database class for managing image records.
 *
 * This class defines the database schema and handles versioning.
 * - v1: Initial schema with a simple key.
 * - v2: Adds a `group` field and a compound primary key `[group+key]`.
 */
class ImageDB extends Dexie {
  /** The table for storing image records. */
  images!: Table<ImageRecord, [string, string]>;

  constructor() {
    super("muse-images");

    // v1
    this.version(1).stores({
      images: "key, savedAt, type, width, height, source",
    });

    // v2
    this.version(2)
      .stores({
        images:
          "&[group+key], group, savedAt, width, height, source, [group+savedAt]",
      })
      .upgrade(async (tx) => {
        await tx
          .table("images")
          .toCollection()
          .modify((obj: { group?: string; [key: string]: any }) => {
            obj.group ??= "default";
          });
      });

    // v3
    this.version(3)
      .stores({
        images:
          "&[group+key], group, savedAt, width, height, source, [group+savedAt], [group+order]",
      })
      .upgrade(async (tx) => {
        await tx
          .table("images")
          .toCollection()
          .modify((obj: { order?: number; savedAt: number }) => {
            obj.order ??= obj.savedAt;
          });
      });
  }
}

let _db: ImageDB | null = null;

/**
 * Lazily initialises and returns the Dexie database instance.
 * Returns `null` when IndexedDB is not available (e.g. some in-app browsers).
 */
function getDB(): ImageDB | null {
  if (_db) return _db;
  if (!isIndexedDBAvailable()) return null;
  _db = new ImageDB();
  return _db;
}

/** Re-exported for consumers that access `db` directly. */
export const db = new Proxy({} as ImageDB, {
  get(_target, prop, receiver) {
    const instance = getDB();
    if (!instance) {
      throw new Error("IndexedDB is not available in this browser.");
    }
    return Reflect.get(instance, prop, receiver);
  },
});

// Best-effort: reduce eviction
(async () => {
  try {
    if (!isIndexedDBAvailable()) return;
    if (navigator.storage?.persist) {
      const persisted = await navigator.storage.persisted();
      if (!persisted) await navigator.storage.persist();
    }
  } catch {}
})();

// ---- Public API  ----

/**
 * Downloads an image from a URL and saves it to the IndexedDB.
 * @param url The URL of the image to download.
 * @param key The key to store the image under. Defaults to the URL.
 * @param groupName The group to store the image in.
 * @param init Optional request init options.
 * @returns A promise that resolves to `true` if the image was saved, or `false` if it already existed.
 */
export async function saveFromURL(
  url: string,
  key: string = url,
  groupName: string = "default",
  identifier: string = "unknown-identifier",
  order: number,
  itemSku: string = "",
  init?: RequestInit
): Promise<boolean> {
  const instance = getDB();
  if (!instance) return false;

  // Get the images record
  const existing = await instance.images.get([groupName, key]);

  // Return false if already exists
  if (existing) {
    return false;
  }

  // Fetch the image
  const res = await fetch(url, { method: "HEAD", ...init, cache: "no-store" });
  if (!res.ok) throw new Error(`Fetch failed: ${res.status} ${res.statusText}`);

  // Create the record
  const rec: ImageRecord = {
    group: groupName,
    identifier: identifier,
    itemSku,
    key,
    savedAt: Date.now(),
    source: url,
    order,
    width: 1080,
    height: 1080,
  };

  await instance.images.put(rec);
  return true;
}

/**
 * Retrieves a single image record from the database.
 * @param groupName The group the image belongs to.
 * @param key The key of the image.
 * @returns A promise that resolves to the `ImageRecord` or `undefined` if not found.
 */
export async function getRecord(
  groupName: string,
  key: string
): Promise<ImageRecord | undefined> {
  const instance = getDB();
  if (!instance) return undefined;
  return instance.images.get([groupName, key]);
}

/**
 * Lists all image record summaries for a given group, sorted by save date (newest first).
 * @param groupName The group to list records for.
 * @returns A promise that resolves to an array of `ImageRecord` objects.
 */
export async function listRecords(groupName: string): Promise<ImageRecord[]> {
  const instance = getDB();
  if (!instance) return [];
  const rows = await instance.images
    .where("[group+order]")
    .between([groupName, Dexie.minKey], [groupName, Dexie.maxKey])
    .toArray();
  return rows.map(({ ...summary }) => summary);
}

/**
 * Lists all image keys for a given group.
 * @param groupName The group to list keys for.
 * @returns A promise that resolves to an array of image keys.
 */
export async function listKeys(groupName: string): Promise<string[]> {
  const instance = getDB();
  if (!instance) return [];
  const ids = (await instance.images
    .where("group")
    .equals(groupName)
    .primaryKeys()) as [string, string][];
  return ids.map(([, key]) => key);
}

/**
 * Removes a single image record from the database.
 * @param groupName The group the image belongs to.
 * @param key The key of the image to remove.
 */
export async function remove(groupName: string, key: string): Promise<void> {
  const instance = getDB();
  if (!instance) return;
  await instance.images.delete([groupName, key]);
}

/**
 * Removes all image records within a specific group.
 * @param groupName The group to clear.
 */
export async function clearGroup(groupName: string): Promise<void> {
  const instance = getDB();
  if (!instance) return;
  const ids = (await instance.images
    .where("group")
    .equals(groupName)
    .primaryKeys()) as [string, string][];
  if (ids.length) await instance.images.bulkDelete(ids);
}

/**
 * Removes all image records from the database.
 */
export async function clearAll(): Promise<void> {
  const instance = getDB();
  if (!instance) return;
  await instance.images.clear();
}

/**
 * Estimates the available storage quota and usage for IndexedDB.
 * @returns A promise that resolves to an object containing usage and quota estimates.
 */
export async function storageEstimate(): Promise<{
  usage?: number;
  quota?: number;
}> {
  try {
    const est = await navigator.storage.estimate?.();
    return { usage: est?.usage, quota: est?.quota };
  } catch {
    return {};
  }
}

// -------- TOC integration  --------

/**
 * Defines the request body for fetching a Table of Contents (TOC).
 */
export type TocRequest = {
  cover_url?: string;
  rear_cover_url?: string;
  inside_left_url?: string;
  inside_right_url?: string;
  locale: string;
  format: string;
};

/**
 * Represents a single entry in the Table of Contents.
 */
export type TocEntry = {
  url: string;
  info?: { pageName?: string; formatIdentifier: string; itemSku: string };
};

/**
 * Represents the full response from the TOC service.
 */
export type TocResponse = { toc: TocEntry[] };

/**
 * Fetches the Table of Contents (TOC) from the content server.
 * @param body The request body for the TOC endpoint.
 * @returns A promise that resolves to an array of `TocEntry` objects.
 * @throws Will throw an error if the TOC request fails.
 */
export async function fetchTOC(
  body: TocRequest,
  group: "muse-giftwraps" | "muse-formats"
): Promise<TocEntry[]> {
  // This function is used in Next13 (pages preview)
  // and Next14 (ingest re-request). To make the environment
  // work in both contexts, we also use `next-runtime-env` here.

  // TODO: Convert to param once we fix the issue with Docker env vars
  const base = env("NEXT_PUBLIC_MU2_URL") || process.env.NEXT_PUBLIC_MU2_URL;

  const res = await fetch(`${base}/product/${group}/toc`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(
      `TOC request failed: ${res.status} ${res.statusText}${text ? ` — ${text}` : ""}`
    );
  }

  const data = (await res.json()) as TocResponse;
  return data.toc ?? [];
}
